import type { DestinationTone } from './DestinationTile'

/**
 * Canonical contractor-portal rail. Both the authenticated Sidebar and the
 * decorative signup preview consume this list so destination order, labels,
 * and PageHeaderIcon route keys cannot drift apart.
 *
 * The rail reads as four runs, each carrying one tone on its destination
 * tiles (the design system's rail `data-tone` names): the day's work first and
 * unlabeled, then reaching customers, then the business itself. Scheduling is
 * last on purpose — Calendar and Jobs are being retired, so they keep a place
 * without earning a better one.
 */
export const PORTAL_NAV_GROUPS = [
  {
    id: 'day',
    label: null,
    tone: 'day',
    items: [
      { label: 'Home', href: '/home' },
      { label: 'Needs your attention', href: '/queue' },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    tone: 'reach',
    items: [
      { label: 'Calls', href: '/calls' },
      { label: 'Messages', href: '/messages' },
      { label: 'Customers', href: '/customers-v2' },
      { label: 'Forms', href: '/forms' },
    ],
  },
  {
    id: 'business',
    label: 'Your business',
    tone: 'business',
    items: [
      { label: 'Brain', href: '/aria' },
      { label: 'Money', href: '/money' },
      { label: 'Invoices', href: '/invoices' },
    ],
  },
  {
    id: 'scheduling',
    label: 'Scheduling',
    tone: 'plan',
    items: [
      { label: 'Calendar', href: '/calendar' },
      { label: 'Reminders', href: '/reminders' },
      { label: 'Jobs', href: '/jobs' },
    ],
  },
] as const satisfies readonly PortalNavGroup[]

export interface PortalNavGroup {
  id: string
  /** Sentence-case Ten Four text label; the first run has none. */
  label: string | null
  tone: DestinationTone
  items: readonly { label: string; href: string }[]
}

export interface PortalNavItem {
  label: string
  href: string
  group: string
  tone: DestinationTone
}

/**
 * Older addresses that still render a rail destination: notifications and old
 * links open /conversations threads, and customer records keep /customers/:id.
 */
const NAV_ALIASES: Readonly<Record<string, readonly string[]>> = {
  '/messages': ['/conversations'],
  '/customers-v2': ['/customers'],
}

/** True on the destination, any of its detail pages, or an alias of either. */
export function isPortalNavActive(pathname: string, href: string): boolean {
  return [href, ...(NAV_ALIASES[href] ?? [])].some(
    (base) => pathname === base || pathname.startsWith(`${base}/`),
  )
}

/** Every destination in rail order, each tagged with its group and tone. */
export const PORTAL_NAV_ITEMS: readonly PortalNavItem[] = (
  PORTAL_NAV_GROUPS as readonly PortalNavGroup[]
).flatMap((group) =>
  group.items.map((item) => ({ ...item, group: group.id, tone: group.tone })),
)
